import { useState, useEffect } from "react";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { HeaderOptionsPopover } from "@/components/HeaderOptionsPopover";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { FileText, Download, Trash2, Crown, MoreVertical, FileEdit, Plus, Share2, Edit3 } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Pricing } from "@/components/Pricing";
import { ShareDialog } from "@/components/ShareDialog";
import { soundManager } from "@/utils/sounds";
import { SEO } from "@/components/SEO";
import { useTranslation } from "react-i18next";
import {AutoTranslate} from "@/lib/autoTranslate.tsx"
import { hasActiveProAccess } from "@/lib/subscription";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { getQueuedSavedResumes } from "@/lib/offlineResumeQueue";


interface SavedResume {
  id: string;
  title: string;
  content: any;
  created_at: string;
  updated_at: string;
}

interface UserProfile {
  subscription_tier: string | null;
  subscription_end?: string | null;
  subscription_canceled?: boolean | null;
}

function parseResumeContent(content: any) {
  if (typeof content !== 'string') return content || {};

  try {
    return JSON.parse(content);
  } catch {
    return { additionalInfo: content };
  }
}

function getSavedResumeTemplateId(content: any): string {
  const templateId =
    content?.selectedTemplateId ||
    content?.templateId ||
    content?.template?.id ||
    content?.formatting?.templateId;

  return typeof templateId === 'string' && templateId.trim()
    ? templateId.trim()
    : 'professional-classic';
}

function getDownloadTemplateId(templateId: string): string {
  return templateId === 'keep-original' ? 'professional-classic' : templateId;
}

export default function SavedResumes() {
  const [resumes, setResumes] = useState<SavedResume[]>([]);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [accessLoading, setAccessLoading] = useState(true);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [editingResume, setEditingResume] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState("");
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [selectedResumeForShare, setSelectedResumeForShare] = useState<{ id: string; title: string } | null>(null);
  const [showFormatDialog, setShowFormatDialog] = useState(false);
  const [selectedResumeForDownload, setSelectedResumeForDownload] = useState<SavedResume | null>(null);
  const { user } = useAuth();
  const isOnline = useOnlineStatus();
  const { t, i18n } = useTranslation();
  const sidebarText = t('sidebar', { returnObjects: true });
  const autoTranslateText = t('autoTranslate', { returnObjects: true });

  const proFeature = autoTranslateText["Pro Feature"] || AutoTranslate(`Pro Feature`);
  const savedResumes = sidebarText["savedResumes"] || AutoTranslate(`Saved Resumes`);
  const upgradeToPro = autoTranslateText["Upgrade to Pro"] || AutoTranslate(`Upgrade to Pro`);
  const saveUnlimitedResumes = autoTranslateText["Save unlimited resumes and access them anytime. Upgrade to Pro to unlock this feature."] || AutoTranslate(`Save unlimited resumes and access them anytime. Upgrade to Pro to unlock this feature.`);
  const chooseYourPlan = autoTranslateText["Choose Your Plan"] || AutoTranslate(`Choose Your Plan`);
  const upgradeToUnlock = autoTranslateText["Upgrade to unlock saved resumes and more features"] || AutoTranslate(`Upgrade to unlock saved resumes and more features`);
  const addAResume = autoTranslateText["Add a resume"] || AutoTranslate(`Add a resume`);
  const loadingYourResumes = autoTranslateText["Loading your resumes..."] || AutoTranslate(`Loading your resumes...`);
  const noFilesSaved = autoTranslateText["No files saved yet"] || AutoTranslate(`No files saved yet`);
  const createYourFirst = autoTranslateText["Create your first resume to get started"] || AutoTranslate(`Create your first resume to get started`);
  const createResume = autoTranslateText["Create Resume"] || AutoTranslate(`Create Resume`);
  const lastUpdated = autoTranslateText["Last updated: "] || AutoTranslate(`Last updated: `);
  const save = autoTranslateText["Save"] || AutoTranslate(`Save`);
  const cancel = autoTranslateText["Cancel"] || AutoTranslate(`Cancel`);
  const download = autoTranslateText["Download"] || AutoTranslate(`Download`);


  useEffect(() => {
    if (user) {
      if (isOnline) {
        checkSubscriptionAndFetch();
      } else {
        fetchOfflineResumes();
      }
    } else {
      setUserProfile(null);
      setResumes([]);
      setAccessLoading(false);
      setLoading(false);
    }
  }, [isOnline, user]);

  const fetchOfflineResumes = async () => {
    if (!user) return;

    setAccessLoading(false);
    setLoading(true);
    setShowUpgradeDialog(false);

    try {
      const queued = await getQueuedSavedResumes(user.id);
      setResumes(queued.map((resume) => ({
        id: resume.id,
        title: `${resume.title} (offline)`,
        content: parseResumeContent(resume.content),
        created_at: resume.createdAt,
        updated_at: resume.updatedAt,
      })));
      setUserProfile({ subscription_tier: "pro" });
    } catch (error) {
      console.error("Error fetching offline resumes:", error);
      setResumes([]);
    } finally {
      setLoading(false);
    }
  };

  const checkSubscriptionAndFetch = async () => {
    setAccessLoading(true);
    setLoading(true);
    setShowUpgradeDialog(false);

    const resumesPromise = fetchResumes();

    // Check live subscription status before deciding whether to show the Pro gate.
    try {
      const { error } = await supabase.functions.invoke('check-subscription');
      if (error) throw error;
    } catch (error) {
      console.error('Error checking subscription:', error);
    }

    // Then fetch the profile after any backend subscription sync has run.
    await fetchUserProfile();
    await resumesPromise;
    setAccessLoading(false);
  };

  const fetchUserProfile = async () => {
    if (!user) return;

    try {
      // Always fetch fresh data from database, not cached
      const { data, error } = await supabase
        .from('profiles')
        .select('subscription_tier, subscription_end, subscription_canceled')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      setUserProfile(data);

      setShowUpgradeDialog(!hasActiveProAccess(data));
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setUserProfile(null);
    }
  };

  const fetchResumes = async () => {
    if (!user) return;

    setLoading(true);
    const { data, error } = await supabase
      .from('saved_resumes')
      .select('*')
      .eq('user_id', user.id)
      .order('updated_at', { ascending: false });

    if (data) {
      setResumes(data.map((resume) => ({
        ...resume,
        content: parseResumeContent(resume.content),
      })));
    }
    setLoading(false);
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase
      .from('saved_resumes')
      .delete()
      .eq('id', id);

    if (!error) {
      setResumes(resumes.filter(resume => resume.id !== id));
    }
  };

  const handleRename = async (id: string) => {
    if (!newTitle.trim()) return;

    const { error } = await supabase
      .from('saved_resumes')
      .update({ title: newTitle.trim() })
      .eq('id', id);

    if (!error) {
      setResumes(resumes.map(resume =>
        resume.id === id ? { ...resume, title: newTitle.trim() } : resume
      ));
      setEditingResume(null);
      setNewTitle("");
    }
  };

  const startRename = (resume: SavedResume) => {
    setEditingResume(resume.id);
    setNewTitle(resume.title);
  };

  const handleShare = (resume: SavedResume) => {
    setSelectedResumeForShare({ id: resume.id, title: resume.title });
    setShareDialogOpen(true);
  };

  const handleDownload = (resume: SavedResume) => {
    setSelectedResumeForDownload(resume);
    setShowFormatDialog(true);
  };

  const handleFormatSelection = async (format: 'pdf' | 'docx') => {
    setShowFormatDialog(false);
    
    if (!selectedResumeForDownload) return;

    try {
      const resumeContent = parseResumeContent(selectedResumeForDownload.content);
      const templateId = getSavedResumeTemplateId(resumeContent);
      const downloadTemplateId = getDownloadTemplateId(templateId);
      const resumeForDownload = {
        ...resumeContent,
        selectedTemplateId: templateId,
        templateId,
      };

      // Debug: Log the actual resume data structure
      console.log('SAVED RESUME DATA STRUCTURE:', resumeForDownload);
      console.log('DATA TYPE:', typeof resumeForDownload);
      console.log('DATA KEYS:', Object.keys(resumeForDownload || {}));
      console.log('SAVED RESUME TEMPLATE:', downloadTemplateId);
      
      // Import template generators dynamically
      const { generateResumeWithTemplate } = await import('@/lib/templateGenerators');
      
      await generateResumeWithTemplate(downloadTemplateId, format, resumeForDownload);
      
      soundManager.signIn();
    } catch (error) {
      console.error('Download error:', error);
      // Fallback to basic document generation
      try {
        const resumeContent = parseResumeContent(selectedResumeForDownload.content);
        const templateId = getDownloadTemplateId(getSavedResumeTemplateId(resumeContent));
        const resumeForDownload = {
          ...resumeContent,
          selectedTemplateId: templateId,
          templateId,
        };

        if (format === 'docx') {
          const { generateResumeWithTemplate } = await import('@/lib/templateGenerators');
          await generateResumeWithTemplate(templateId, 'docx', resumeForDownload);
        } else {
          const { generateResumeWithTemplate } = await import('@/lib/templateGenerators');
          generateResumeWithTemplate(templateId, 'pdf', resumeForDownload);
        }
        soundManager.signIn();
      } catch (fallbackError) {
        console.error('Fallback download error:', fallbackError);
        alert('Download failed. Please try again.');
      }
    } finally {
      setSelectedResumeForDownload(null);
    }
  };

  const canAccessSavedResumes = hasActiveProAccess(userProfile);

  if (!accessLoading && !canAccessSavedResumes) {
    return (
      <SidebarProvider>
        <div className="min-h-screen flex w-full bg-gradient-main">
          <AppSidebar />

        <main className="flex-1 flex flex-col relative z-10">
            <header className="flex items-center justify-between p-4 border-b border-border bg-card/80 backdrop-blur-sm">
              <div className="flex items-center gap-4">
                <SidebarTrigger className="hover:bg-primary-light" />
                <div className="flex items-center gap-2">
                  <Crown className="h-6 w-6 text-yellow-500" />
                  <h1 className="text-xl font-bold text-foreground">{savedResumes}</h1>
                  <Badge variant="secondary">{proFeature}</Badge>
                </div>
              </div>
              <HeaderOptionsPopover />
            </header>

            <div className="flex-1 flex items-center justify-center p-6">
              <Card className="max-w-md mx-auto text-center bg-card/80 backdrop-blur-sm border-card-border">
                <CardHeader>
                  <Crown className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
                  <CardTitle>{upgradeToPro}</CardTitle>
                  <CardDescription>
                    {saveUnlimitedResumes}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
                    <DialogTrigger asChild>
                      <Button className="w-full">
                        <Crown className="h-4 w-4 mr-2" />
                        {upgradeToPro}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>{chooseYourPlan}</DialogTitle>
                        <DialogDescription>
                          {upgradeToUnlock}
                        </DialogDescription>
                      </DialogHeader>
                      <Pricing onPaymentStart={() => setShowUpgradeDialog(false)} />
                    </DialogContent>
                  </Dialog>
                </CardContent>
              </Card>
            </div>

          <Footer />
        </main>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <SEO
        title="My Saved Resumes — FIND Resume Builder"
        description="Manage your saved AI-generated resumes. Edit, download, share, or delete past resumes from your FIND Resume Builder dashboard."
        path="/saved-resumes"
      />
      <div className="min-h-screen flex w-full bg-gradient-main">
        <AppSidebar />

        <main className="flex-1 flex flex-col relative z-10">
          <header className="flex items-center justify-between p-4 border-b border-border bg-card/80 backdrop-blur-sm">
            <div className="flex items-center gap-4">
              <SidebarTrigger className="hover:bg-primary-light" />
              <div className="flex items-center gap-2">
                <FileText className="h-6 w-6 text-primary" />
                <h1 className="text-xl font-bold text-foreground">{savedResumes}</h1>
                <Badge variant="secondary" className="text-xs whitespace-nowrap">{resumes.length} saved</Badge>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <HeaderOptionsPopover />
            </div>
          </header>

          <div className="flex-1 p-6">
            {accessLoading || loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                <p className="text-muted-foreground mt-4">{loadingYourResumes}</p>
              </div>
            ) : resumes.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <h2 className="text-2xl font-semibold text-foreground mb-2">{noFilesSaved}</h2>
                <p className="text-muted-foreground mb-6">{createYourFirst}</p>
                <Button onClick={() => window.location.href = '/'}>
                  <Plus className="h-4 w-4 mr-2" />
                  {createResume}
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {resumes.map((resume) => (
                  <Card key={resume.id} className="bg-card/80 backdrop-blur-sm border-card-border hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="h-5 w-5 text-primary" />
                        {resume.title}
                      </CardTitle>
                      <CardDescription>
                        {lastUpdated} {new Date(resume.updated_at).toLocaleDateString()}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-2">
                        {editingResume === resume.id ? (
                          <div className="flex-1 flex gap-2">
                            <Input
                              value={newTitle}
                              onChange={(e) => setNewTitle(e.target.value)}
                              className="flex-1"
                              autoFocus
                            />
                            <Button size="sm" onClick={() => handleRename(resume.id)}>
                              {save}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingResume(null);
                                setNewTitle("");
                              }}
                            >
                              {cancel}
                            </Button>
                          </div>
                        ) : (
                          <>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="flex-1"
                              onClick={() => handleDownload(resume)}
                            >
                              <Download className="h-4 w-4 mr-2" />
                              {download}
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size="sm" variant="outline" aria-label="More options" disabled={!isOnline}>
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent>
                                <DropdownMenuItem onClick={() => startRename(resume)}>
                                  <Edit3 className="h-4 w-4 mr-2" />
                                  {AutoTranslate(`Rename`)}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => window.location.href = `/resume-editor/${resume.id}`}>
                                  <FileEdit className="h-4 w-4 mr-2" />
                                  {AutoTranslate(`Edit`)}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleShare(resume)}>
                                  <Share2 className="h-4 w-4 mr-2" />
                                  {AutoTranslate(`Share`)}
                                </DropdownMenuItem>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      {AutoTranslate(`Delete`)}
                                    </DropdownMenuItem>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>{AutoTranslate(`Delete Resume`)}</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        {AutoTranslate(`Are you sure you want to delete this resume? This action cannot be undone.`)}
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>{AutoTranslate(`Cancel`)}</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => handleDelete(resume.id)}
                                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                      >
                                        {AutoTranslate(`Delete`)}
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          <Footer />
        </main>
      </div>

      {selectedResumeForShare && (
        <ShareDialog
          open={shareDialogOpen}
          onOpenChange={setShareDialogOpen}
          resumeId={selectedResumeForShare.id}
          resumeTitle={selectedResumeForShare.title}
        />
      )}

      {/* Format Selection Dialog */}
      <Dialog open={showFormatDialog} onOpenChange={setShowFormatDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Choose Download Format</DialogTitle>
            <DialogDescription>
              Select the format you'd like to download your resume in.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <Button
              onClick={() => handleFormatSelection('docx')}
              className="flex flex-col items-center gap-2 h-20"
              variant="outline"
            >
              <FileText className="h-6 w-6" />
              <span>Word Document</span>
              <span className="text-xs text-muted-foreground">(.docx)</span>
            </Button>
            <Button
              onClick={() => handleFormatSelection('pdf')}
              className="flex flex-col items-center gap-2 h-20"
              variant="outline"
            >
              <FileText className="h-6 w-6" />
              <span>PDF Document</span>
              <span className="text-xs text-muted-foreground">(.pdf)</span>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Floating Create Resume Button */}
      <div className="fixed bottom-40 lg:bottom-60 right-6 z-40">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="lg"
                variant="outline"
                onClick={() => window.location.href = '/'}
                className="rounded-full h-14 w-14 flex items-center justify-center p-0 shadow-lg hover:shadow-xl transition-shadow"
                aria-label="Create new resume"
              >
                <Plus className="h-6 w-6" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p>{addAResume}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </SidebarProvider>
  );
}
